import { type ComponentPropsWithoutRef, type ReactNode } from 'react';
import * as RDM from '@radix-ui/react-dropdown-menu';
import * as RD from '@radix-ui/react-dialog';
import * as RT from '@radix-ui/react-tooltip';
import * as RP from '@radix-ui/react-popover';
import { Check, X } from 'lucide-react';
import { cn } from './cn.js';
import { Button } from './Button.js';

/**
 * Overlay primitives. All four share one visual recipe — hairline border,
 * raised surface, large shadow, and the same scale-and-lift animation keyed
 * off Radix's data-state — so a menu, a popover and a dialog feel like the
 * same system rather than three libraries.
 */

const surfaceOverlay = 'rounded-lg border border-line bg-surface-raised shadow-lg';

// ---------------------------------------------------------------------------
// Menu
// ---------------------------------------------------------------------------

export const Menu = RDM.Root;
export const MenuTrigger = RDM.Trigger;

export function MenuContent({
  children,
  className,
  align = 'end',
  sideOffset = 6,
  ...rest
}: ComponentPropsWithoutRef<typeof RDM.Content>) {
  return (
    <RDM.Portal>
      <RDM.Content
        align={align}
        sideOffset={sideOffset}
        collisionPadding={8}
        className={cn(
          'anim-content z-50 min-w-[12rem] overflow-hidden p-1',
          surfaceOverlay,
          className,
        )}
        {...rest}
      >
        {children}
      </RDM.Content>
    </RDM.Portal>
  );
}

export function MenuItem({
  children,
  className,
  icon,
  danger,
  shortcut,
  ...rest
}: ComponentPropsWithoutRef<typeof RDM.Item> & {
  icon?: ReactNode;
  danger?: boolean;
  shortcut?: string;
}) {
  return (
    <RDM.Item
      className={cn(
        'flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none',
        'data-[highlighted]:bg-surface-hover',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-45',
        danger && 'text-danger data-[highlighted]:bg-danger-tint',
        className,
      )}
      {...rest}
    >
      {icon && (
        <span aria-hidden className="shrink-0 text-ink-tertiary [&>svg]:h-3.5 [&>svg]:w-3.5">
          {icon}
        </span>
      )}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {shortcut && <Kbd className="ml-2">{shortcut}</Kbd>}
    </RDM.Item>
  );
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <RDM.Label className="px-2 pb-1 pt-1.5 text-2xs font-semibold uppercase tracking-wide text-ink-tertiary">
      {children}
    </RDM.Label>
  );
}

export function MenuSeparator() {
  return <RDM.Separator className="my-1 h-px bg-line-subtle" />;
}

export function MenuRadioItem({
  children,
  className,
  ...rest
}: ComponentPropsWithoutRef<typeof RDM.RadioItem>) {
  return (
    <RDM.RadioItem
      className={cn(
        'relative flex cursor-default select-none items-center gap-2 rounded-sm py-1.5 pl-7 pr-2 text-sm outline-none',
        'data-[highlighted]:bg-surface-hover',
        className,
      )}
      {...rest}
    >
      <RDM.ItemIndicator className="absolute left-2">
        <Check aria-hidden className="h-3.5 w-3.5 text-brand" />
      </RDM.ItemIndicator>
      {children}
    </RDM.RadioItem>
  );
}

export const MenuRadioGroup = RDM.RadioGroup;

// ---------------------------------------------------------------------------
// Dialog
// ---------------------------------------------------------------------------

export const Dialog = RD.Root;
export const DialogTrigger = RD.Trigger;
export const DialogClose = RD.Close;

export function DialogContent({
  children,
  title,
  description,
  className,
  width = 'md',
}: {
  children: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  className?: string;
  width?: 'sm' | 'md' | 'lg';
}) {
  const w = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl' }[width];
  return (
    <RD.Portal>
      <RD.Overlay className="anim-overlay fixed inset-0 z-50 bg-black/35 backdrop-blur-[2px]" />
      <RD.Content
        className={cn(
          'anim-content fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2',
          'max-h-[85vh] overflow-y-auto',
          surfaceOverlay,
          w,
          className,
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b border-line-subtle px-4 py-3">
          <div className="min-w-0">
            <RD.Title className="text-sm font-semibold text-ink">{title}</RD.Title>
            {description && (
              <RD.Description className="mt-1 text-xs leading-relaxed text-ink-secondary">
                {description}
              </RD.Description>
            )}
          </div>
          <RD.Close asChild>
            <Button variant="ghost" size="sm" iconOnly aria-label="Close">
              <X aria-hidden className="h-4 w-4" />
            </Button>
          </RD.Close>
        </div>
        <div className="p-4">{children}</div>
      </RD.Content>
    </RD.Portal>
  );
}

export function DialogFooter({ children }: { children: ReactNode }) {
  return <div className="mt-4 flex items-center justify-end gap-2">{children}</div>;
}

// ---------------------------------------------------------------------------
// Tooltip
// ---------------------------------------------------------------------------

export function TooltipProvider({ children }: { children: ReactNode }) {
  return (
    <RT.Provider delayDuration={350} skipDelayDuration={200}>
      {children}
    </RT.Provider>
  );
}

export function Tooltip({
  content,
  children,
  side = 'top',
  className,
}: {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  className?: string;
}) {
  if (!content) return <>{children}</>;
  return (
    <RT.Root>
      <RT.Trigger asChild>{children}</RT.Trigger>
      <RT.Portal>
        <RT.Content
          side={side}
          sideOffset={6}
          collisionPadding={8}
          className={cn(
            'anim-content z-50 max-w-[18rem] rounded-md bg-ink px-2 py-1 text-2xs leading-snug text-page shadow-md',
            className,
          )}
        >
          {content}
          <RT.Arrow className="fill-[rgb(var(--text))]" width={9} height={4} />
        </RT.Content>
      </RT.Portal>
    </RT.Root>
  );
}

// ---------------------------------------------------------------------------
// Popover
// ---------------------------------------------------------------------------

export const Popover = RP.Root;
export const PopoverTrigger = RP.Trigger;
export const PopoverClose = RP.Close;

export function PopoverContent({
  children,
  className,
  align = 'end',
  ...rest
}: ComponentPropsWithoutRef<typeof RP.Content>) {
  return (
    <RP.Portal>
      <RP.Content
        align={align}
        sideOffset={6}
        collisionPadding={8}
        className={cn('anim-content z-50 w-72 p-3', surfaceOverlay, className)}
        {...rest}
      >
        {children}
      </RP.Content>
    </RP.Portal>
  );
}

// ---------------------------------------------------------------------------

export function Kbd({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <kbd
      className={cn(
        'inline-flex h-4 min-w-4 items-center justify-center rounded-xs border border-line',
        'bg-surface-sunken px-1 font-sans text-3xs font-medium text-ink-tertiary',
        className,
      )}
    >
      {children}
    </kbd>
  );
}
